import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import {
  Storage as StorageIcon,
  Memory as MemoryIcon,
  CloudQueue as CloudQueueIcon,
  DeleteSweep as DeleteSweepIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useRedisCacheStats, useRedisCacheMutations } from "../../../hooks/useRedisCacheAdmin";

const RedisCacheStats: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useRedisCacheStats();
  const { flushAllProducts } = useRedisCacheMutations();

  const stats = data?.data;

  const handleFlushAll = async () => {
    if (
      window.confirm(
        t("admin.redisCache.confirmFlushAll")
      )
    ) {
      await flushAllProducts.mutateAsync();
      refetch();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t("admin.redisCache.noStatisticsAvailable")}
      </Typography>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <StorageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.redisCache.totalKeys")}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.totalKeys.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CloudQueueIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.redisCache.productKeys")}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.productKeys.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocalShippingIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.redisCache.shippingKeys")}
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.shippingKeys.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MemoryIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.redisCache.memoryUsed")}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.memoryUsedHuman}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("admin.redisCache.bytesCount", { count: stats.memoryUsed })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CloudQueueIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  {t("admin.redisCache.redisInfo")}
                </Typography>
              </Box>
              <Typography variant="body1">
                {t("admin.redisCache.version")}: {stats.redisVersion}
              </Typography>
              <Typography variant="body1">
                {t("admin.redisCache.connections")}: {stats.totalConnections.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ backgroundColor: "#fff3cd" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: "#856404" }}>
                {t("admin.redisCache.dangerZone")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "#856404" }}>
                {t("admin.redisCache.flushWarning")}
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleFlushAll}
              >
                {t("admin.redisCache.flushAllProductCache")}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RedisCacheStats;

